import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { tokenStorage } from '@/api/client';
import { getMe } from '@/api/account';
import { useDiveCentersStore } from '@/stores/diveCenters';
import type { Provider, ProviderUser } from '@/types';

const SESSION_KEY = 'bt_provider_session';

interface CachedSession {
    provider: Provider;
    providerUser: ProviderUser;
}

/**
 * Authentication store.
 *
 * Persists a lightweight copy of `provider` + `providerUser` to
 * localStorage so we can render the navbar / dashboard immediately on
 * reload without a round-trip. The actual source of truth (token + DB)
 * stays server-side; we refresh from `/account/me` on mount.
 *
 * Reactivity note:
 *   `tokenStorage` lives in localStorage (not reactive). To make
 *   `isAuthenticated` recompute the moment the token changes, we keep
 *   a mirror `accessTokenRef` here that is set in lock-step with
 *   localStorage. The computed depends on this ref, not on the
 *   tokenStorage getter — that getter cannot trigger Vue's reactivity
 *   system on its own.
 */
export const useAuthStore = defineStore('auth', () => {
    const provider = ref<Provider | null>(null);
    const providerUser = ref<ProviderUser | null>(null);
    const accessTokenRef = ref<string | null>(tokenStorage.getAccess());

    const isAuthenticated = computed(
        () => !!accessTokenRef.value && !!providerUser.value,
    );

    function setSession(
        p: Provider,
        pu: ProviderUser,
        accessToken: string,
        refreshToken: string,
    ) {
        provider.value = p;
        providerUser.value = pu;
        tokenStorage.set(accessToken, refreshToken);
        accessTokenRef.value = accessToken;
        localStorage.setItem(SESSION_KEY, JSON.stringify({ provider: p, providerUser: pu }));
    }

    function logout() {
        provider.value = null;
        providerUser.value = null;
        tokenStorage.clear();
        accessTokenRef.value = null;
        localStorage.removeItem(SESSION_KEY);
        // Clear cached centers so a different account doesn't see stale data.
        useDiveCentersStore().reset();
    }

    /** Loads cached session from localStorage. Safe to call multiple times. */
    function hydrate() {
        // Pick up any token written by another tab / refresh cycle.
        accessTokenRef.value = tokenStorage.getAccess();

        if (provider.value && providerUser.value) return;
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw) as CachedSession;
            provider.value = parsed.provider;
            providerUser.value = parsed.providerUser;
        } catch {
            localStorage.removeItem(SESSION_KEY);
        }
    }

    /** Re-fetches authoritative session data from the server. */
    async function refreshMe() {
        try {
            const data = await getMe();
            provider.value = data.provider;
            providerUser.value = data.providerUser;
            localStorage.setItem(
                SESSION_KEY,
                JSON.stringify({ provider: data.provider, providerUser: data.providerUser }),
            );
        } catch {
            // 401 already triggered the global redirect via api/client.ts
        }
    }

    return {
        provider,
        providerUser,
        isAuthenticated,
        setSession,
        logout,
        hydrate,
        refreshMe,
    };
});
